import * as React from "react";
import {VeboxForm} from "../../components/form/VeboxForm";
import {veboxDefaultTheme} from "../../index";
import {ThemeProvider} from "@vebox/etui-system";
import {ProcessHeader, ProcessIndicator, ProcessIndicatorStep, ProcessPage} from "../Processes";
import {RestDataLoader} from "../../dashboard/widgets/unilever/RestDataLoader";
import {uuid, VeboxModels} from "../../types/veboxModels";
import {VeboxOverlay} from "../../components/overlay/VeboxOverlay";
import {VeBoxModal} from "../../components/modal/VeBoxModal";
import {VeboxUser} from "../../providers/VeboxUser";
import {UserInfoStage} from "./stages/Info";
import {RolesAndPermissions} from "./stages/Roles";
import {commercialHierarchyGUID, VisibilityStage} from "./stages/Visibility";
import {AppTranslations} from "../../components/Utils/translations/AppTranslations";

import "./UserCreation.scss";

declare var AppTranslations: AppTranslations;

export enum UserCreationState { INITIAL, FAILURE, PENDING, SUCCESSFUL}

export enum ValueToUpload {
    ROLES = "roles",
    HIERARCHIES = "hierarchies",
    USER_INFO = "user_info",
    INVISIBLE_USER = "invisible_user"
}

export const UserCreation: React.FC = () => {

    const overlayController = VeboxOverlay.useTarget();

    const [values, setValue] = React.useState<any>({});
    const [user, setUser] = React.useState<VeboxModels.User>();

    const [creation, setCreationState] = React.useState<UserCreationState>();

    const finishProcess = () => {
        setCreationState(UserCreationState.PENDING);
        try {
            const {selectedHierarchies, selectedCommercialHierarchies} = values.hierarchies;
            const newUserRequest: UserCreationProps = {
                challenge: selectedHierarchies === "all_companies"
                    ? user.company.guid
                    : selectedHierarchies[0] === commercialHierarchyGUID
                        ? user.company.parentGuid ?? user.company.guid
                        : last(selectedHierarchies ?? []),
                challengeType: Challenge.COMPANY,
                ...values.user_info,
                locale: values.user_info.locale.value,
                timezone: values.user_info.timezone.value,
                roles: values.roles,
                receiveEmailNotifications: values.newsletter ?? false,
                receivePushNotifications: values.push_notification ?? false,
                commercialNodesGuids: selectedHierarchies[0] !== commercialHierarchyGUID
                    ? []
                    : commercialHierarchyTreeToList(selectedCommercialHierarchies ?? []),
                visible: !values.invisible_user ?? true
            };
            createUser(newUserRequest)
                .then(_ => {
                    setCreationState(UserCreationState.SUCCESSFUL);
                    getUserCount()
                        .then(count => {
                            if (count !== getCurrentUserCountFromDOM()) {
                                setUserCountInDOM(count)
                            }
                        })
                        .catch(console.error)

                    setTimeout(() => {
                        setValue({
                            user_info: {},
                            hierarchies: [],
                            commercialHierarchies: [],
                            roles: [],
                        });
                        setCreationState(UserCreationState.INITIAL);
                    }, 2000);
                })
                .catch((e) => {

                    setCreationState(UserCreationState.FAILURE);

                    const close = () => {
                        setCreationState(UserCreationState.INITIAL);
                        overlayController?.clear();
                    };

                    overlayController.show(<ErrorDialog onClose={close}/>);
                });
        } catch (e) {

            setCreationState(UserCreationState.FAILURE);

            const close = () => {
                setCreationState(UserCreationState.INITIAL);
                overlayController?.clear();
            };

            overlayController.show(<ErrorDialog onClose={close}/>);
        }
    };

    return (
        <ThemeProvider theme={veboxDefaultTheme}>
            <VeboxUser.Provider onReceiveUserData={setUser}> {/* TODO: One day (when Vebox is re-written in React) it should be moved to the root of the app*/}
                <ProcessPage className={"flx-col"} total={3}>
                    <ProcessHeader processTitle={AppTranslations.userCreationProcess.title}>
                        <ProcessIndicator>
                            <ProcessIndicatorStep>{AppTranslations.userCreationProcess.infoStageIndicator}</ProcessIndicatorStep>
                            <ProcessIndicatorStep>{AppTranslations.userCreationProcess.visibilityStageIndicator}</ProcessIndicatorStep>
                            <ProcessIndicatorStep>{AppTranslations.userCreationProcess.rolesStageIndicator}</ProcessIndicatorStep>
                        </ProcessIndicator>
                    </ProcessHeader>
                    <VeboxOverlay.Target {...overlayController.targetProps} className={"process-stages flx-row"}>
                        <VeboxForm onChange={setValue} values={values}>
                            <UserInfoStage/>
                            <VisibilityStage creationState={creation}/>
                            <RolesAndPermissions onCreateUser={finishProcess} creationState={creation}/>
                        </VeboxForm>
                    </VeboxOverlay.Target>
                </ProcessPage>
            </VeboxUser.Provider>
        </ThemeProvider>
    );
}

interface ErrorDialogProps {
    onClose: () => void;
}

const ErrorDialog: React.FC<ErrorDialogProps> = ({onClose}) => {
    return (
        <VeBoxModal.Backdrop>
            <VeBoxModal style={{width: 300}}>
                <VeBoxModal.Header title={AppTranslations.userCreationProcess.errorDialogTitle} onClose={onClose}/>
                <VeBoxModal.Section>
                    {AppTranslations.userCreationProcess.errorDialogMessage}
                </VeBoxModal.Section>
            </VeBoxModal>
        </VeBoxModal.Backdrop>
    )
}

enum Challenge { DEVICE_PIN = "DEVICE_PIN", MANUFACTURER_CODE = "MANUFACTURER_CODE", COMPANY = "COMPANY"}

interface UserCreationProps {
    challenge: string;
    challengeType: Challenge;
    company?: VeboxModels.Company;
    commercialNodesGuids: uuid[];
    email: string;
    locale: string;
    name: string;
    privacyPolicyOK?: boolean;
    receiveEmailNotifications: boolean;
    receivePushNotifications: boolean;
    roles: string[];
    surname: string;
    termsOfServiceOK?: boolean;
    termsOfServiceRequired?: boolean;
    timezone: string;
    username: string;
    visible: boolean;
}

function createUser(data: UserCreationProps) {
    return new Promise((res, rej) => {
        RestDataLoader.postJSON(
            "users",
            data,
            res,
            rej
        )
    });
}

export function last<T>(list: T[]) {
    if (!Array.isArray(list) || list.length === 0) {
        return undefined;
    }
    return list[list.length - 1];
}

type CommercialHierarchyNode = {
    value: string,
    children: (string | CommercialHierarchyNode)[],
}

export function commercialHierarchyTreeToList(hierarchyTree: CommercialHierarchyNode[]) {
    const treeLeafReducer = (partial: string[], ht: string | CommercialHierarchyNode) => {
        if (typeof ht === 'string') {
            return [...partial, ht];
        }
        return [...partial, ...ht.children.reduce(treeLeafReducer, [] as string[])]
    };

    return hierarchyTree.reduce(treeLeafReducer, [] as string[]);
}

function getUserCount() {
    return new Promise<number>((res, rej) => {
        RestDataLoader.loadData(
            "users",
            {},
            ({total}) => res(total),
            rej
        )
    })
}

function getCurrentUserCountFromDOM() {
    return Number.parseInt($("#usersCount").data("users-count"), 10)
}

function setUserCountInDOM(count: number) {
    const userCountDOM = $("#usersCount")
    userCountDOM.attr("data-users-count", count)
    userCountDOM.text(count)
}